// Libs
import React, { useEffect, useState } from 'react';
import { Tooltip, Modal } from 'antd';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import * as Yup from 'yup';

// Own Libs
import { Button, FormDatePicker, FormSelect, FormSubmitButton, FormTextArea, MasterDetailForm, Row } from '@digital/ui';

//Api
import { getRodadaNegociacoes, GetSasConsicaoPagamentoOptions, ISasPropostaOrdemCompra, ISasRodadaNegociacoes, responderRodadaNegociacoes, updateRodadaNegociacoes } from '@app/api/sas/sasProposta';
import { getArquivosDocumentoOrdemCompraByOc } from '@app/api/sas/sasDocumentoOrdemCompra';

//Files
import { ModalInclusaoMultAnexo, Space, UploadMultipleTableTmp, Visibility } from '@app/components';
import { dadosGeraisState, editandoState, modalAcoesIdState, ordemCompraRodadaNegociacaoIdState, stepState } from '../atoms';

//Utils
import { vtObjToFormData } from '@app/helpers/objHelper';
import { EGuidTipoSasDocumento } from '@app/enums/EGuidTipoSasDocumento';
import { EStatusRodadaNegociacoes } from '@app/enums';
import { optionsSwitch, optionsVigenciaArquivoDocumentoOrdemCompra } from '@app/helpers/optionsSwitch';
import CurrencyInput from '@app/components/CurrencyInput';
import ModalConfirmContentJustificativa from '@app/components/ModalConfirmContent/ModalConfirmContentJustificativa';
import { ETipoDemanda } from '@app/enums/ETipoDemanda';

interface Props {
    sasProposta: ISasPropostaOrdemCompra;
}

export const BotoesSasPropostaRodadaNegociacao = ({ sasProposta }: Props) => {
    const setModalAcoesId = useSetRecoilState(modalAcoesIdState);
    const editando = useRecoilValue(editandoState);

    const step = useRecoilValue(stepState);
    const dadosGerais = useRecoilValue(dadosGeraisState);

    const [exibirModal, setExibirModal] = useState(false);
    const [initialValues, setInitialValues] = useState<ISasRodadaNegociacoes>({} as ISasRodadaNegociacoes);

    const ehAditivo = dadosGerais.tipo === ETipoDemanda.Aditivo;

    const [load, setLoad] = useState(false);

    useEffect(() => {
        getRodadaNegociacoes(sasProposta.id, step.ordemCompraRodadaNegociacaoId)
            .then((resp: any) => {
                resp.sasPropostaId = sasProposta.id;
                setInitialValues(resp);
            });
    }, [load, step.ordemCompraRodadaNegociacaoId]);


    const handleCancel = () => {
        setExibirModal(false);
    };

    const handleCancelAndClose = () => {
        setExibirModal(false);
        setModalAcoesId('');
    };

    const handleSubmit = (values: any) => {
        const form = vtObjToFormData(values);
        form.append('sasPropostaId', sasProposta.id);
        form.append('ordemCompraRodadaNegociacaoId', step.ordemCompraRodadaNegociacaoId || '');

        if (editando.sasPropostaId == sasProposta.id) {
            Modal.confirm({
                title: <>{editando.justificativa}</>,
                content: <ModalConfirmContentJustificativa
                    texto='É necessário informar a justificativa para a alteração.'
                    setJustificativa={e => {
                        form.set('justificativaEdicao', e);
                    }}
                />,
                okText: 'Confirmar',
                cancelText: 'Cancelar',
                onOk: async (e) => {
                    form.append('editando', 'true');
                    return new Promise<boolean>((resolve, reject) => {
                        updateRodadaNegociacoes(form)
                            .then((data: any) => {
                                if (data && data.valid) {
                                    handleCancelAndClose();
                                    return resolve(true);
                                } else {
                                    if (data.notifications && data.notifications.length > 0) {
                                        const erroJustificativaEdicao = data.notifications.findIndex((c: any) => {
                                            return c.property == 'justificativaEdicao';
                                        });

                                        if (erroJustificativaEdicao >= 0) {
                                            return reject(false);
                                        }
                                        else {
                                            return resolve(false);
                                        }
                                    }
                                }
                            });
                    });
                },
                onCancel: handleCancelAndClose
            });
        } else {
            updateRodadaNegociacoes(form)
                .then((data: any) => {
                    if (data && data.valid) {
                        handleCancelAndClose();
                    }
                });
        }
    };


    const rejeitarRodadaNegociacoes = async (idRecord: string, values: any): Promise<boolean> => {
        const form = vtObjToFormData(values);

        if (editando.sasPropostaId == sasProposta.id) {
            Modal.confirm({
                title: <>{editando.justificativa}</>,
                content: <ModalConfirmContentJustificativa
                    texto='É necessário informar a justificativa para a alteração.'
                    setJustificativa={e => {
                        form.set('justificativaEdicao', e);
                    }}
                />,
                okText: 'Confirmar',
                cancelText: 'Cancelar',
                onOk: async (e) => {
                    form.append('editando', 'true');
                    return new Promise<boolean>((resolve, reject) => {
                        responderRodadaNegociacoes(idRecord, step.ordemCompraRodadaNegociacaoId || "", form)
                            .then((data: any) => {
                                if (data && data.valid) {
                                    handleCancelAndClose();
                                    return resolve(true);
                                } else {
                                    if (data.notifications && data.notifications.length > 0) {
                                        const erroJustificativaEdicao = data.notifications.findIndex((c: any) => {
                                            return c.property == 'justificativaEdicao';
                                        });
                                        if (erroJustificativaEdicao >= 0) {
                                            return reject(false);
                                        }
                                    }
                                }
                            });
                    });
                },
                onCancel: handleCancelAndClose
            });
        } else {
            return await responderRodadaNegociacoes(idRecord, step.ordemCompraRodadaNegociacaoId || "", form)
                .then((data: any) => {
                    if (data && data.valid) {
                        handleCancelAndClose();
                        return true;
                    }
                    return false;
                });
        }

        return true;
    };

    return (
        <Visibility visible={true}>
            <Row align='middle' justify='center' style={{ display: 'flex' }} >
                <Visibility
                    visible={initialValues.status !== EStatusRodadaNegociacoes.NAO_PARTICIPANTE}>
                    <Tooltip title="Aprovar Status da Rodada">
                        <Button icon='commons-ok' onClick={() => {
                            setExibirModal(true);
                            setLoad(!load);
                        }} />
                    </Tooltip>
                </Visibility>

                <Visibility visible={
                    initialValues.status !== EStatusRodadaNegociacoes.CONCLUIDO ||
                    editando.sasPropostaId === sasProposta.id
                } >
                    <Space ml={4} />
                    <ModalInclusaoMultAnexo
                        buttonIcon="commons-cancel"
                        buttonTooltip='Recusar Status da Rodada'
                        onSubmit={async (values) => { return await rejeitarRodadaNegociacoes(sasProposta.id, values); }}
                        isMultAnexo={true}
                        showJustificativa={true}
                        textButtonSave="Salvar"
                        showButtonSave={
                            [EStatusRodadaNegociacoes.CONCLUIDO, EStatusRodadaNegociacoes.NAO_PARTICIPANTE].toString().includes(initialValues.status) == false
                            || editando.sasPropostaId === sasProposta.id
                        }
                        requiredAnexos={false}
                        initialValues={{
                            justificativa: initialValues.comentario
                        }}
                        onGetArquivos={async () => {
                            setLoad(!load);
                            return await getArquivosDocumentoOrdemCompraByOc({
                                ordemCompraId: sasProposta.ordemCompraId,
                                sasTipoDocumentoId: EGuidTipoSasDocumento.RecusaAnexoSasPropostaRodadaNegociacao,
                                sasPropostaId: sasProposta.id,
                                ordemCompraRodadaNegociacaoId: step.ordemCompraRodadaNegociacaoId
                            }).then((data: any) => {
                                return data || [];
                            });
                        }}
                        disabledFields={
                            (
                                initialValues.status == EStatusRodadaNegociacoes.CONCLUIDO ||
                                initialValues.status == EStatusRodadaNegociacoes.NAO_PARTICIPANTE
                            ) && editando.sasPropostaId !== sasProposta.id
                        }
                    />
                </Visibility>
            </Row>

            <Visibility visible={exibirModal} >

                <Modal
                    title="Rodada de Negociações"
                    width="980px"
                    visible={exibirModal}
                    onCancel={handleCancel}
                    footer={null}
                >
                    <MasterDetailForm
                        key={`formStatusRodada_${sasProposta.id}`}
                        initialValues={initialValues}
                        actions={[
                            <Button title='fechar' type='link' onClick={() => handleCancel()} />,
                            <Visibility
                                visible={
                                    [
                                        EStatusRodadaNegociacoes.CONCLUIDO,
                                        EStatusRodadaNegociacoes.NAO_PARTICIPANTE
                                    ].toString().includes(initialValues.status) == false ||
                                    editando.sasPropostaId === sasProposta.id
                                }
                            >
                                <FormSubmitButton title='Salvar' type='primary' noIcon onClick={handleSubmit} />
                            </Visibility>
                        ]}
                        validationSchema={Yup.object().shape({
                            dataSolicitacao: Yup.date().required('O campo é obrigatório.'),
                            dataPrevisaoRetorno: Yup.date().required('O campo é obrigatório.')
                                .when('dataSolicitacao', (dataSolicitacao) => {
                                    if (dataSolicitacao) {
                                        return Yup.date()
                                            .min(dataSolicitacao, 'O campo deve ser maior que a data de solicitação.')
                                            .typeError('Data inválida.');
                                    }
                                }),
                            dataRecebimentoDaProposta: Yup.date()
                                .when('dataSolicitacao', (dataSolicitacao) => {
                                    if (dataSolicitacao) {
                                        return Yup.date()
                                            .min(dataSolicitacao, 'O campo deve ser maior que a data de solicitação.')
                                            .typeError('Data inválida.');
                                    }
                                }),
                            valorAtualizado: Yup.number()
                                .typeError('Valor inválido')
                                .min(0, 'O valor não pode ser menor que zero.')
                        })}
                    >
                        <Row>
                            <FormDatePicker
                                label='Data da solicitação'
                                name='dataSolicitacao'
                                format='DD/MM/YYYY'
                                span={4}
                                disabled={
                                    (
                                        initialValues.status == EStatusRodadaNegociacoes.CONCLUIDO ||
                                        initialValues.status == EStatusRodadaNegociacoes.NAO_PARTICIPANTE
                                    ) && editando.sasPropostaId !== sasProposta.id
                                }
                            />

                            <FormDatePicker
                                label='Data previsão retorno'
                                name='dataPrevisaoRetorno'
                                format='DD/MM/YYYY'
                                span={4}
                                disabled={
                                    (
                                        initialValues.status == EStatusRodadaNegociacoes.CONCLUIDO ||
                                        initialValues.status == EStatusRodadaNegociacoes.NAO_PARTICIPANTE
                                    ) && editando.sasPropostaId !== sasProposta.id
                                }
                            />

                            <FormDatePicker
                                label='Data recebimento da proposta'
                                name='dataRecebimentoDaProposta'
                                format='DD/MM/YYYY'
                                span={4}
                                disabled={
                                    (
                                        initialValues.status == EStatusRodadaNegociacoes.CONCLUIDO ||
                                        initialValues.status == EStatusRodadaNegociacoes.NAO_PARTICIPANTE
                                    ) && editando.sasPropostaId !== sasProposta.id
                                }
                            />

                            <FormTextArea
                                name='comentario'
                                label='Comentário'
                                span={12}
                                disabled={
                                    (
                                        initialValues.status == EStatusRodadaNegociacoes.CONCLUIDO ||
                                        initialValues.status == EStatusRodadaNegociacoes.NAO_PARTICIPANTE
                                    ) && editando.sasPropostaId !== sasProposta.id
                                }
                            />

                            <FormSelect
                                label='Condição de Pagamento'
                                name="sasCondicaoPagamentoId"
                                getOptions={GetSasConsicaoPagamentoOptions}
                                span={ehAditivo ? 8 : 6}
                                disabled={
                                    (
                                        initialValues.status == EStatusRodadaNegociacoes.CONCLUIDO ||
                                        initialValues.status == EStatusRodadaNegociacoes.NAO_PARTICIPANTE
                                    ) && editando.sasPropostaId !== sasProposta.id
                                }
                            />
                            <CurrencyInput
                                label='Valor atualizado'
                                name="valorAtualizado"
                                span={ehAditivo ? 4 : 3}
                                disabled={
                                    (
                                        initialValues.status == EStatusRodadaNegociacoes.CONCLUIDO ||
                                        initialValues.status == EStatusRodadaNegociacoes.NAO_PARTICIPANTE
                                    ) && editando.sasPropostaId !== sasProposta.id
                                }
                            />
                            {
                                !ehAditivo &&
                                <FormSelect
                                    label='Proposta Equalizada?'
                                    name="propostaEqualizada"
                                    options={
                                        [,
                                            { text: 'Não foi possível Equalizar', value: 2 },
                                            { text: 'Sim', value: 1 },
                                            { text: 'Não', value: 0 }
                                        ]
                                    }
                                    span={3}
                                    disabled={
                                        (
                                            initialValues.status == EStatusRodadaNegociacoes.CONCLUIDO ||
                                            initialValues.status == EStatusRodadaNegociacoes.NAO_PARTICIPANTE
                                        ) && editando.sasPropostaId !== sasProposta.id
                                    }
                                />
                            }

                        </Row>
                        <Row>
                            <UploadMultipleTableTmp
                                name='arquivos'
                                idDoComponente={`formHomologacaoUpload_${sasProposta.id}`}
                                type='primary'
                                tablePagination={false}
                                onGetArquivos={async () => {
                                    return await getArquivosDocumentoOrdemCompraByOc({
                                        ordemCompraId: sasProposta.ordemCompraId,
                                        sasTipoDocumentoId: EGuidTipoSasDocumento.AceiteAnexoSasPropostaRodadaNegociacao,
                                        sasPropostaId: sasProposta.id,
                                        ordemCompraRodadaNegociacaoId: step.ordemCompraRodadaNegociacaoId
                                    }).then((data: any) => {
                                        return data || [];
                                    });
                                }}
                                switchOptionsStatusArquivo={optionsVigenciaArquivoDocumentoOrdemCompra}
                                disabled={
                                    (
                                        initialValues.status == EStatusRodadaNegociacoes.CONCLUIDO ||
                                        initialValues.status == EStatusRodadaNegociacoes.NAO_PARTICIPANTE
                                    ) && editando.sasPropostaId !== sasProposta.id
                                }
                            />
                        </Row>
                    </MasterDetailForm>
                </Modal>
            </Visibility>
        </Visibility>
    );
};